import { isBrokerPersona } from '../constants/personas.js';
import { TOUR_IDS } from './onboardingConfig.js';
import { isTourDone } from './onboardingStorage.js';
import { STEP_CONTEXTUAL_TOUR } from './onboardingNavigation.js';

/**
 * @typedef {import('./onboardingConfig.js').TourId} TourId
 * @typedef {import('./onboardingStorage.js').TourProgressMap} TourProgressMap
 *
 * @typedef {{
 *   currentStep: string,
 *   persona?: string,
 *   activePropertyId?: string | null,
 *   activeScenarioId?: string | null,
 *   tourProgress: TourProgressMap,
 *   locale?: string,
 *   hasProperties?: boolean,
 * }} TourContext
 *
 * @typedef {{
 *   id: string,
 *   messageKey: string,
 *   tourId?: TourId,
 *   action?: 'startTour' | 'navigate',
 *   targetStep?: string,
 * }} AssistantSuggestion
 *
 * @typedef {{
 *   id: string,
 *   labelKey: string,
 *   done: boolean,
 * }} ChecklistItem
 */

/** @type {Record<string, TourId[]>} Priority when user has no properties yet */
const PERSONA_TOUR_PRIORITY = {
  investisseur: [
    TOUR_IDS.WELCOME,
    TOUR_IDS.CREATE_PROJECT,
    TOUR_IDS.DASHBOARD,
    TOUR_IDS.ANALYSIS,
    TOUR_IDS.REPORT,
    TOUR_IDS.SETTINGS,
  ],
  courtier_immo: [
    TOUR_IDS.WELCOME,
    TOUR_IDS.CREATE_PROJECT,
    TOUR_IDS.DASHBOARD,
    TOUR_IDS.ANALYSIS,
    TOUR_IDS.REPORT,
    TOUR_IDS.SETTINGS,
  ],
  courtier_hypo: [
    TOUR_IDS.WELCOME,
    TOUR_IDS.CREATE_PROJECT,
    TOUR_IDS.DASHBOARD,
    TOUR_IDS.ANALYSIS,
    TOUR_IDS.REPORT,
    TOUR_IDS.SETTINGS,
  ],
  preteur_prive: [
    TOUR_IDS.WELCOME,
    TOUR_IDS.SETTINGS,
    TOUR_IDS.CREATE_PROJECT,
    TOUR_IDS.DASHBOARD,
    TOUR_IDS.ANALYSIS,
    TOUR_IDS.REPORT,
  ],
};

/**
 * Resolve i18n key with optional persona suffix (`baseKey.persona`).
 * @param {string} baseKey
 * @param {string | undefined} persona
 * @param {(key: string) => string} t
 * @returns {string}
 */
export function resolvePersonaTextKey(baseKey, persona, t) {
  if (!persona) return baseKey;
  const personaKey = `${baseKey}.${persona}`;
  const translated = t(personaKey);
  return translated !== personaKey ? personaKey : baseKey;
}

/**
 * @param {string} baseKey
 * @param {string | undefined} persona
 * @param {(key: string) => string} t
 * @returns {{ title: string, description: string }}
 */
export function resolvePersonaCopy(titleKey, descriptionKey, persona, t) {
  const resolvedTitleKey = resolvePersonaTextKey(titleKey, persona, t);
  const resolvedDescKey = resolvePersonaTextKey(descriptionKey, persona, t);
  return {
    title: t(resolvedTitleKey),
    description: t(resolvedDescKey),
  };
}

/**
 * Pick the most relevant pending tour for the user.
 * @param {TourContext} ctx
 * @returns {TourId | null}
 */
export function suggestNextTour(ctx) {
  const {
    tourProgress,
    currentStep,
    persona = 'investisseur',
    hasProperties = false,
    activeScenarioId,
  } = ctx;

  const contextual = STEP_CONTEXTUAL_TOUR[currentStep];
  if (contextual && !isTourDone(tourProgress, contextual)) {
    return contextual;
  }

  if (currentStep === 'home' && !hasProperties && !isTourDone(tourProgress, TOUR_IDS.CREATE_PROJECT)) {
    return TOUR_IDS.CREATE_PROJECT;
  }

  if (currentStep === 'dashboard' && !isTourDone(tourProgress, TOUR_IDS.DASHBOARD)) {
    return TOUR_IDS.DASHBOARD;
  }

  if (currentStep === 'scenario' && !isTourDone(tourProgress, TOUR_IDS.ANALYSIS)) {
    return TOUR_IDS.ANALYSIS;
  }

  if (currentStep === 'report' && !isTourDone(tourProgress, TOUR_IDS.REPORT)) {
    return TOUR_IDS.REPORT;
  }

  if (activeScenarioId && !isTourDone(tourProgress, TOUR_IDS.REPORT)) {
    return TOUR_IDS.REPORT;
  }

  const order = PERSONA_TOUR_PRIORITY[persona] || PERSONA_TOUR_PRIORITY.investisseur;
  for (const tourId of order) {
    if (!isTourDone(tourProgress, tourId)) return tourId;
  }

  return null;
}

/**
 * @param {TourContext} ctx
 * @returns {ChecklistItem[]}
 */
export function getOnboardingChecklist(ctx) {
  const { hasProperties = false, activeScenarioId, tourProgress } = ctx;
  return [
    {
      id: 'createDeal',
      labelKey: 'tour.checklist.createDeal',
      done: Boolean(hasProperties) || isTourDone(tourProgress, TOUR_IDS.CREATE_PROJECT),
    },
    {
      id: 'runScenario',
      labelKey: 'tour.checklist.runScenario',
      done: Boolean(activeScenarioId) || isTourDone(tourProgress, TOUR_IDS.ANALYSIS),
    },
    {
      id: 'exportPdf',
      labelKey: 'tour.checklist.exportPdf',
      done: isTourDone(tourProgress, TOUR_IDS.REPORT),
    },
  ];
}

/**
 * Rule-based contextual tips (replaceable by AI via fetchAiAssistantSuggestions).
 * @param {TourContext} ctx
 * @returns {AssistantSuggestion[]}
 */
export function getRuleBasedSuggestions(ctx) {
  const { currentStep, persona, hasProperties, tourProgress, activePropertyId } = ctx;
  /** @type {AssistantSuggestion[]} */
  const tips = [];

  const next = suggestNextTour(ctx);
  if (next) {
    tips.push({
      id: 'next-tour',
      messageKey: `tour.assistant.suggest.${next}`,
      tourId: next,
      action: 'startTour',
    });
  }

  if (currentStep === 'home' && !hasProperties) {
    tips.push({
      id: 'first-deal',
      messageKey: isBrokerPersona(persona)
        ? 'tour.assistant.tip.firstDealBroker'
        : 'tour.assistant.tip.firstDeal',
      tourId: TOUR_IDS.CREATE_PROJECT,
      action: 'startTour',
    });
  }

  if (persona === 'preteur_prive' && currentStep === 'home') {
    tips.push({
      id: 'lender-home',
      messageKey: 'tour.assistant.tip.lenderHome',
      targetStep: 'home',
    });
  }

  if (persona === 'courtier_immo' && activePropertyId && !isTourDone(tourProgress, TOUR_IDS.REPORT)) {
    tips.push({
      id: 'broker-pdf',
      messageKey: 'tour.assistant.tip.brokerPdf',
      tourId: TOUR_IDS.REPORT,
      action: 'startTour',
    });
  }

  return tips.slice(0, 3);
}

/**
 * Stub for future AI integration. Returns null until a backend is wired.
 * @returns {Promise<AssistantSuggestion[] | null>}
 */
export async function fetchAiAssistantSuggestions() {
  return null;
}

/**
 * Combined assistant payload for the floating panel.
 * @param {TourContext} ctx
 * @param {(key: string) => string} t
 * @returns {Promise<{
 *   greetingKey: string,
 *   suggestions: AssistantSuggestion[],
 *   checklist: ChecklistItem[],
 *   suggestedTour: TourId | null,
 * }>}
 */
export async function getAssistantPayload(ctx, t) {
  const persona = ctx.persona || 'investisseur';
  const greetingKey = resolvePersonaTextKey('tour.assistant.greeting', persona, t);

  const ruleSuggestions = getRuleBasedSuggestions(ctx);
  const aiSuggestions = await fetchAiAssistantSuggestions(ctx);
  const suggestions = aiSuggestions?.length ? aiSuggestions : ruleSuggestions;

  return {
    greetingKey,
    suggestions,
    checklist: getOnboardingChecklist(ctx),
    suggestedTour: suggestNextTour(ctx),
  };
}

/** @typedef {'started' | 'completed' | 'skipped' | 'step_next' | 'step_prev'} TourEventAction */

/**
 * Lightweight event bus for analytics / future AI context.
 */
export function createTourEventBus() {
  /** @type {Set<(payload: object) => void>} */
  const listeners = new Set();

  return {
    /**
     * @param {(payload: object) => void} fn
     * @returns {() => void}
     */
    subscribe(fn) {
      listeners.add(fn);
      return () => listeners.delete(fn);
    },
    /**
     * @param {object} payload
     */
    emit(payload) {
      listeners.forEach((fn) => {
        try {
          fn(payload);
        } catch (e) {
          console.warn('[onboarding] tour event listener error', e);
        }
      });
    },
  };
}

export const tourEventBus = createTourEventBus();
